import React, { useEffect, useState } from "react";
import { Crown, Sparkles } from "lucide-react";
import { useLang, t } from "../lib/i18n";
import { relationLabel } from "../lib/relations";

/**
 * A royal proclamation card shown right after a guest registers.
 * Announces "The Royal Family has arrived" and names their bond with Anagha.
 */
export default function RoyalArrival({ name, roleKey, lovingTitle }) {
  const lang = useLang();
  const [entered, setEntered] = useState(false);

  useEffect(() => {
    const id = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(id);
  }, []);

  return (
    <section
      data-testid="royal-arrival-card"
      className={`relative mx-auto max-w-3xl px-6 sm:px-10 py-8 sm:py-10 rounded-[2.25rem] overflow-hidden transition-all duration-700 ${
        entered ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"
      }`}
      style={{
        background:
          "linear-gradient(140deg, rgba(74,22,53,0.96) 0%, rgba(43,10,32,0.98) 55%, rgba(20,6,16,0.99) 100%)",
        border: "1px solid rgba(212,175,55,0.45)",
        boxShadow:
          "0 30px 80px -20px rgba(74,22,53,0.65), inset 0 1px 0 rgba(212,175,55,0.35)",
      }}
    >
      {/* Gold corner ornaments */}
      <CornerOrnament className="top-3 left-3" />
      <CornerOrnament className="top-3 right-3" flip="x" />
      <CornerOrnament className="bottom-3 left-3" flip="y" />
      <CornerOrnament className="bottom-3 right-3" flip="xy" />

      {/* Sparkle field */}
      <div className="pointer-events-none absolute inset-0 opacity-70" aria-hidden="true">
        <span className="absolute top-6 left-1/4 w-1 h-1 rounded-full bg-amber-200 animate-pulse" />
        <span className="absolute top-10 right-1/3 w-1.5 h-1.5 rounded-full bg-rose-200 animate-pulse" style={{ animationDelay: "0.5s" }} />
        <span className="absolute bottom-8 left-1/3 w-1 h-1 rounded-full bg-amber-100 animate-pulse" style={{ animationDelay: "1s" }} />
        <span className="absolute bottom-14 right-1/4 w-1 h-1 rounded-full bg-fuchsia-200 animate-pulse" style={{ animationDelay: "1.4s" }} />
      </div>

      <div className="relative text-center">
        {/* Proclamation eyebrow */}
        <p
          className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-[0.7rem] tracking-[0.28em] uppercase text-amber-100/90 border border-amber-300/40 bg-black/30 backdrop-blur"
          style={{ fontFamily: "'Tiro Telugu','Noto Sans Telugu',serif" }}
        >
          <Crown size={13} className="text-amber-300" />
          {t("arrival_proclaim", lang)}
          <Crown size={13} className="text-amber-300" />
        </p>

        {/* Main royal title */}
        <h2
          data-testid="royal-arrival-title"
          className="mt-4 text-3xl sm:text-4xl md:text-5xl leading-tight"
          style={{
            fontFamily: "'Yatra One','Tiro Telugu','Noto Sans Telugu',serif",
            background: "linear-gradient(180deg,#FFE9A8 0%,#D4AF37 55%,#8A6A1A 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            textShadow: "0 0 40px rgba(212,175,55,0.25)",
          }}
        >
          👑 {t("arrival_royal_family", lang)}
        </h2>

        {/* Guest identity block */}
        <div className="mt-6 flex flex-col items-center">
          <div className="relative">
            <p
              className="text-2xl sm:text-3xl text-amber-50"
              style={{ fontFamily: "'Yatra One','Tiro Telugu',serif" }}
              data-testid="royal-arrival-name"
            >
              {name}
            </p>
            {/* Underline flourish */}
            <span
              className="block mx-auto mt-1 w-24 h-[2px] rounded-full"
              style={{
                background:
                  "linear-gradient(90deg, transparent 0%, #D4AF37 50%, transparent 100%)",
              }}
            />
          </div>

          {/* Relation chip */}
          <span
            className="mt-3 inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-sm sm:text-base text-amber-50 border border-amber-300/45 bg-amber-500/10"
            style={{ fontFamily: "'Tiro Telugu','Noto Sans Telugu',serif" }}
            data-testid="royal-arrival-relation"
          >
            <Sparkles size={14} className="text-amber-300" />
            {relationLabel(roleKey, lang)}
          </span>

          {/* Loving royal title (optional) */}
          {lovingTitle && (
            <p
              className="mt-4 text-lg sm:text-xl italic text-rose-100/95"
              style={{ fontFamily: "'Tiro Telugu','Noto Sans Telugu',serif" }}
              data-testid="royal-arrival-loving-title"
            >
              — {lovingTitle} —
            </p>
          )}
        </div>

        {/* Welcome line */}
        <p
          className="mt-6 text-sm sm:text-base text-amber-100/85"
          style={{ fontFamily: "'Tiro Telugu','Noto Sans Telugu',serif" }}
        >
          {t("arrival_welcome", lang)} · {t("arrival_next_chapter", lang)}
        </p>
      </div>
    </section>
  );
}

function CornerOrnament({ className = "", flip }) {
  const transform =
    flip === "x" ? "scaleX(-1)" :
    flip === "y" ? "scaleY(-1)" :
    flip === "xy" ? "scale(-1,-1)" : "none";
  return (
    <svg
      className={`absolute ${className} pointer-events-none`}
      width="42" height="42" viewBox="0 0 42 42" fill="none"
      style={{ transform }}
      aria-hidden="true"
    >
      <path
        d="M4 4 L4 20 M4 4 L20 4 M4 4 C 14 8, 14 14, 20 20"
        stroke="#D4AF37"
        strokeWidth="1.6"
        strokeLinecap="round"
        fill="none"
        opacity="0.75"
      />
      <circle cx="4" cy="4" r="2.4" fill="#D4AF37" opacity="0.6" />
    </svg>
  );
}
